import React, { useEffect, useState } from "react";
import { Box, Stack } from "@mui/material";
import { useReduxPrefState } from "../../app/hooks";
import { inGamut, OKLCh } from "../../oklab";
import OKLChPicker from "../widgets/OKLChPicker";
import OptionSelect from "../widgets/OptionSelect";
import SectionTitle from "../widgets/SectionTitle";
import StoneSearch from "./StoneSearch";
import { useDiamondOptions } from "./StoneSearch/useDiamondOptions";

// The colour area, the picker's controls, the filters and the tabs above the gallery.
const HEADER_HEIGHT = 360;

// A middling lightness at a middling chroma: somewhere with colour in it, so the first search says
// something, rather than the black the axes start at.
const OPENING_COLOR: OKLCh = { l: 0.7, c: 0.1, h: 60 };

// Pick a stone from the stock by colour: a colour in OKLCh, which orders the results, and any
// combination of the colour systems to narrow them.
const VisualPicker = () => {
  const options = useDiamondOptions();

  // The colour the picker opens on is the last one searched, so coming back to the page resumes
  // where it was left.
  const [startingColor, setStartingColor] = useReduxPrefState(
    "startingOKColor",
    OPENING_COLOR,
  );
  const [color, setColor] = useState<OKLCh>(startingColor ?? OPENING_COLOR);
  const [effectiveColor, setEffectiveColor] = useState<OKLCh>(
    startingColor ?? OPENING_COLOR,
  );
  const [fancyIntensity, setFancyIntensity] = useState<string | null>(null);
  const [fancySaturation, setFancySaturation] = useState<string | null>(null);
  const [fancyOvertone, setFancyOvertone] = useState<string | null>(null);
  const [fancyColor, setFancyColor] = useState<string | null>(null);
  const [langermanColor, setLangermanColor] = useState<string | null>(null);
  const [flat, setFlat] = useState<string | null>(null);
  const [melee, setMelee] = useState<string | null>(null);

  // The picker moves freely and settles into the query after a beat, so a drag does not fire a
  // search per pixel.
  useEffect(() => {
    const settle = setTimeout(() => {
      setEffectiveColor(color);
      setStartingColor(color);
    }, 500);
    return () => clearTimeout(settle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [color]);

  const searchedColor = inGamut(effectiveColor);

  return (
    <StoneSearch
      options={options}
      headerHeight={HEADER_HEIGHT}
      search={{
        // The area's far side is past what sRGB can show; the colour searched for is the one it
        // displays there, which is also the one the picker reads out.
        okL: searchedColor.l,
        okC: searchedColor.c,
        okH: searchedColor.h,
        fancyIntensity,
        fancySaturation,
        fancyOvertone,
        fancyColor,
        langermanColor,
        flat: flat ? Number(flat) : null,
        melee: melee ? Number(melee) : null,
      }}
      colorArea={() => (
        <Box>
          <SectionTitle>Color</SectionTitle>
          <Stack direction="row" spacing={2} alignItems="flex-start">
            <OKLChPicker value={color} onChange={setColor} />
            <Stack spacing={2} sx={{ flex: 1, minWidth: 0 }}>
              {/* The parts a colour name is built from — "Fancy", "Brownish Orangy",
                  "Pink" — each fixed or left open on its own, so a search can be every Fancy
                  Intense stone, or every Pink one, or both at once. The values are whatever the
                  colour table holds, so retiring a colour there retires it here. */}
              <Stack direction="row" sx={{ flexWrap: "wrap", gap: 2 }}>
                <OptionSelect
                  label="Intensity"
                  options={options?.fancyIntensities ?? []}
                  value={fancyIntensity}
                  onChange={(_, value) => setFancyIntensity(value)}
                  sx={{ minWidth: 180, flex: 1 }}
                />
                <OptionSelect
                  label="Saturation"
                  options={options?.fancySaturations ?? []}
                  value={fancySaturation}
                  onChange={(_, value) => setFancySaturation(value)}
                  sx={{ minWidth: 160, flex: 1 }}
                />
                <OptionSelect
                  label="Overtone"
                  options={options?.fancyOvertones ?? []}
                  value={fancyOvertone}
                  onChange={(_, value) => setFancyOvertone(value)}
                  sx={{ minWidth: 200, flex: 1 }}
                />
                <OptionSelect
                  label="Color"
                  options={options?.fancyColors ?? []}
                  value={fancyColor}
                  onChange={(_, value) => setFancyColor(value)}
                  sx={{ minWidth: 200, flex: 1 }}
                />
              </Stack>
              <Stack direction="row" sx={{ flexWrap: "wrap", gap: 2 }}>
                <OptionSelect
                  label="Langerman color"
                  options={options?.langermanColors ?? []}
                  value={langermanColor}
                  onChange={(_, value) => setLangermanColor(value)}
                  sx={{ minWidth: 200, flex: 1 }}
                />
                <OptionSelect
                  label="Flat"
                  options={options?.flatColors ?? []}
                  value={flat}
                  onChange={(_, value) => setFlat(value)}
                  sx={{ minWidth: 160, flex: 1 }}
                />
                <OptionSelect
                  label="Melee"
                  options={options?.meleeColors ?? []}
                  value={melee}
                  onChange={(_, value) => setMelee(value)}
                  sx={{ minWidth: 160, flex: 1 }}
                />
              </Stack>
            </Stack>
          </Stack>
        </Box>
      )}
    />
  );
};

export default VisualPicker;
